import React from 'react';
import { connect } from 'react-redux';
import * as analytics from '@eb/site-analytics';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import { collections as collectionsDuck } from '@eb/destination-utils';
import { trackLocationChange as trackLocationChangeAction } from '../actions/location';
import { trackEventClickInternal as trackEventClickInternalAction } from '../actions/analytics';
import { saveEvent as saveEventAction } from '../actions/event';
import { getBucketContent, getBrowseSection } from '../utils';
import { logCheckpointEidOnPageChange } from '../utils/janus';
import { getOrganizersFromEvents } from '../selectors/organizers';
import StyledBucket, {
    BUCKET_LOOKUP_MAP,
} from '../components/styledBucket/StyledBucket';

import {
    GA_CITY_BROWSE_CATEGORY,
    ENTITY_TYPE_MAP,
    NON_MANUAL_SOURCES,
    CITY_BROWSE_EVENT_CLICKED,
    CITY_BROWSE_MANUAL_EVENT_CLICKED,
} from '../constants';

const {
    actions: {
        addOrRemoveEventToCollection: addOrRemoveEventToCollectionAction,
        createCollection: createCollectionAction,
    },
    selectors: { getFormattedCollections },
} = collectionsDuck;

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

export class BrowseBucket extends React.Component {
    _handleOnSaveEvent = (eventId, shouldSave) => {
        const { onSaveEvent } = this.props;

        return onSaveEvent(eventId, shouldSave, true);
    };

    _handleTrackEventClickInternal = (data, ticketsBy) => {
        const { domain, trackEventClickInternal } = this.props;

        const janusCheckpoint = includes(NON_MANUAL_SOURCES, ticketsBy)
            ? CITY_BROWSE_EVENT_CLICKED
            : CITY_BROWSE_MANUAL_EVENT_CLICKED;

        logCheckpointEidOnPageChange(janusCheckpoint, domain, data.itemId);
        trackEventClickInternal(data);
    };

    render() {
        const { content, type, template } = this.props;
        const emptyComponent = <div key={type} className="empty-bucket" />;

        /*Future proofing such that if api adds additional buckets app doesn't break*/
        /*
         If content is undefined, that means the bucket type map has the appropriate key,
         but the value associated to it necessary to access the actual content in the response
         has changed. Solution is we should send an error to bugsnag, but not let the app fall over.
        */
        if (type !== 'follow_organizer' && (!content || isEmpty(content))) {
            // logger.error(`Content is undefined in CityBrowse MainContent.js for type: ${type}, template: ${template}`);
            return emptyComponent;
        }

        /* If the passed in type is not known in the ENTITY_TYPE_MAP this means that the API has
        started to return a layout format that is not known. The API should be allowed to progress
        without causing errors so no error should be fired. */
        if (!has(ENTITY_TYPE_MAP, type)) {
            return emptyComponent;
        }

        /* If the passed in template is not known in the BUCKET_LOOKUP_MAP this means that the API has
        started to return a layout format that is not known. The API should be allowed to progress
        without causing errors so no error should be fired. */
        if (!has(BUCKET_LOOKUP_MAP, template)) {
            // logger.error(`Provided template is not known in CityBrowse MainContent.js for type: ${type}, template: ${template}`);
            return emptyComponent;
        }

        return (
            <StyledBucket
                {...this.props}
                onSaveEvent={this._handleOnSaveEvent}
                onTrackEventClickInternal={this._handleTrackEventClickInternal}
            />
        );
    }
}

export const _mapStateToProps = (state, ownProps) => {
    const {
        bucketConfig,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
    } = ownProps;

    const section = getBrowseSection(state.location.isOnline);
    const content = getBucketContent(
        state.content,
        bucketConfig,
        state.currencyFormat,
        section,
    );
    const organizers =
        bucketConfig.type === 'follow_organizer'
            ? getOrganizersFromEvents(state)
            : undefined;

    return {
        content,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        organizers,
        userCollections: state.app.featureFlags.enableUserCollections
            ? getFormattedCollections(state)
            : undefined,
        currentPlace: state.location.currentPlace,
        currentPlaceParent: state.location.currentPlaceParent,
        placeType: state.location.placeType,
        isAuthenticated: state.auth.isAuthenticated,
        shareOptions: state.share.options,
        allEventsUrl: state.content.allEventsUrl,
        httpReferer: state.app.httpReferer,
        locationSlug: state.location.slug,
        domain: state.app.domain,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        userEmail: state.app.userEmail,
        publicId: state.app.publicId,
        userId: state.app.publicId,
        gaCategory: GA_CITY_BROWSE_CATEGORY,
        gaActionSuffix: 'CityBrowse',
        gaSettings: state.app.gaSettings,
        bucketKey: bucketConfig.key,
        trackGAEvent: (action) =>
            trackEventFromState(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        ...bucketConfig,
    };
};

const _mapDispatchToProps = (dispatch) => ({
    onSaveEvent: (...args) => dispatch(saveEventAction(...args)),
    trackLocationChange: (...args) =>
        dispatch(trackLocationChangeAction(...args)),
    onAddOrRemoveEventToCollection: (...args) =>
        dispatch(
            addOrRemoveEventToCollectionAction(GA_CITY_BROWSE_CATEGORY)(
                ...args,
            ),
        ),
    onCreateCollection: (...args) =>
        dispatch(createCollectionAction(GA_CITY_BROWSE_CATEGORY)(...args)),
    trackEventClickInternal: (...args) =>
        dispatch(trackEventClickInternalAction(...args)),
});

export default connect(_mapStateToProps, _mapDispatchToProps)(BrowseBucket);
